// Landing page — ported pixel-true from prototype/configure-a.jsx Landing.
// Light hero with floating Astro mascot + greeting bubble, 3 primary mode
// cards, 3 secondary modes (the 4th slot is a dashed "Quick Build" card),
// recent runs as cards, and footer rollup stats.
//
// Wiring:
//  - "Start" on a primary mode card  → pickMode + go to Configure.
//  - "+ Add to this" / "↻ Refresh" on a recent run → pickMode(continue|refresh, runId).
//  - "Open org ↗" → noop today (real org-link wiring lives in real api).
//  - "Do a Quick Build" → /templates.

import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { api } from "@/lib/api";
import { MODES } from "@/lib/data";
import { useConfigStore } from "@/lib/store/configStore";
import { useAuth } from "@/hooks/useAuth";
import { useRuntime } from "@/hooks/useRuntime";
import type { Mode, ModeOption, Run } from "@/lib/types";

const MODE_ICONS: Record<Mode, ReactNode> = {
  crm_only: <path d="M3 5h18v4H3zM3 11h18v4H3zM3 17h12v2H3z" />,
  tdsx_only: <path d="M12 3l9 5-9 5-9-5zm0 8l9 5-9 5-9-5z" strokeLinejoin="round" />,
  full: <path d="M4 7h4v10H4zm6-3h4v16h-4zm6 6h4v10h-4z" />,
  continue: <path d="M13 2L3 14h7l-1 8 10-12h-7z" strokeLinejoin="round" />,
  refresh: (
    <path
      d="M21 12a9 9 0 11-3-6.7M21 4v5h-5"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  ),
  custom: <path d="M12 2l3 7h7l-5.5 4 2 7L12 16l-6.5 4 2-7L2 9h7z" />,
};

interface ModeAccent {
  bg: string;
  fg: string;
}

const MODE_ACCENTS: Record<Mode, ModeAccent> = {
  crm_only:  { bg: "#e0eef9",  fg: "var(--eb-50)" },
  tdsx_only: { bg: "#dceee5",  fg: "var(--teal)" },
  full:      { bg: "#ede0fb",  fg: "var(--violet)" },
  continue:  { bg: "#fde8c2",  fg: "#8a5200" },
  refresh:   { bg: "#dde9f3",  fg: "var(--eb-30)" },
  custom:    { bg: "var(--cb-95)", fg: "var(--eb-50)" },
};

export function Landing(): JSX.Element {
  const navigate = useNavigate();
  const pickMode = useConfigStore((s) => s.pickMode);
  const auth = useAuth();

  // While the auth probes are in flight, render a stable skeleton so we
  // don't flicker between an unauthed shell and an authed shell.
  if (auth.loading) {
    return <LandingLoading />;
  }

  // Hard gate: the v2 home page is for authenticated SF users. Anyone
  // without an org connection sees only the connect screen — no mode
  // cards, no runs, no fake stats. Curator-only sessions get bounced
  // to the curator console.
  if (!auth.orgConnected) {
    return <ConnectGate connect={auth.connect} curatorOnly={auth.authenticated} />;
  }

  return <LandingAuthed auth={auth} navigate={navigate} pickMode={pickMode} />;
}

interface AuthedProps {
  auth: ReturnType<typeof useAuth>;
  navigate: ReturnType<typeof useNavigate>;
  pickMode: ReturnType<typeof useConfigStore.getState>["pickMode"];
}

function safeHost(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url).host;
  } catch {
    return url.replace(/^https?:\/\//, "").replace(/\/$/, "");
  }
}

function LandingAuthed({ auth, navigate, pickMode }: AuthedProps): JSX.Element {
  const orgHost =
    safeHost(auth.org?.instance_url) ?? auth.org?.org_name ?? "connected org";
  const greeting = auth.userName
    ? auth.userName.split(/\s+/)[0]
    : auth.userEmail?.split("@")[0] ?? "Data Fan";
  const { isTrainingDay } = useRuntime();

  const { data: runs = [] } = useQuery({
    queryKey: ["runs"],
    queryFn: api.listRuns,
    enabled: auth.orgConnected,
  });
  const { data: packs = [] } = useQuery({
    queryKey: ["packs"],
    queryFn: api.listPacks,
  });

  const handleMode = (modeId: Mode, runId?: string): void => {
    pickMode(modeId, runId);
    navigate("/configure");
  };

  const primary = MODES.slice(0, 3);
  const secondary = MODES.slice(3);
  const recent = runs.slice(0, 5);
  const monthRuns = runs.length || 0;
  const profilesCount = packs.length || 0;

  return (
    <div>
      {/* ───────────── HERO ───────────── */}
      <section
        className="hero-gradient"
        style={{ padding: "48px 32px 28px", position: "relative", overflow: "hidden" }}
      >
        <div style={{ maxWidth: 1180, margin: "0 auto", position: "relative" }}>
          {/* Floating Astro */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              right: -8,
              top: -18,
              width: 220,
              height: 220,
              pointerEvents: "none",
              zIndex: 1,
            }}
          >
            <div
              style={{
                position: "absolute",
                inset: "8% 8%",
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, rgba(29,112,201,.16) 0%, rgba(29,112,201,.05) 45%, transparent 70%)",
                filter: "blur(6px)",
              }}
            />
            <img
              src="/assets/astro-waving.png"
              alt=""
              className="astro-bob"
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "contain",
                filter: "drop-shadow(0 18px 24px rgba(3,45,96,.18))",
              }}
            />
            <div
              style={{
                position: "absolute",
                left: -6,
                top: 22,
                background: "white",
                border: "1px solid var(--bd)",
                borderRadius: 14,
                padding: "8px 12px",
                fontSize: 12,
                fontWeight: 600,
                color: "var(--ink)",
                boxShadow: "0 6px 18px -6px rgba(3,45,96,.25)",
                whiteSpace: "nowrap",
              }}
            >
              Hey {greeting} 👋
              <div
                style={{
                  position: "absolute",
                  right: -6,
                  top: "50%",
                  marginTop: -5,
                  width: 10,
                  height: 10,
                  background: "white",
                  borderRight: "1px solid var(--bd)",
                  borderTop: "1px solid var(--bd)",
                  transform: "rotate(45deg)",
                }}
              />
            </div>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 18,
              flexWrap: "wrap",
            }}
          >
            <div
              className="chip"
              style={{ background: "white", borderColor: "var(--bd)" }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 999,
                  background: "var(--teal)",
                }}
              />
              Connected to {orgHost}
            </div>
            <div className="chip ghost" style={{ color: "var(--ink-3)" }}>
              SE tools · v2.1
            </div>
            {/*
              2026-05-03 (training-day): pre-flight pill is the
              single most-asked-for thing during the May-4 training —
              "is my org ready?" — surfaced on Landing so trainees
              don't have to hunt for it. Goes after the "Connected"
              chip but before role-gated chips so it's the first
              actionable thing a freshly-connected user sees.
            */}
            <button
              onClick={() => navigate("/preflight")}
              className="chip ghost"
              style={{
                cursor: "pointer",
                border: 0,
                background: "transparent",
                color: "var(--eb-50)",
                fontWeight: 600,
              }}
              title="Verify org licenses, locale, and feature flags before launch."
            >
              Run pre-flight check →
            </button>
            {auth.isAdmin && (
              <button
                onClick={() => navigate("/admin")}
                className="chip ghost"
                style={{ cursor: "pointer", border: 0, background: "transparent" }}
              >
                Admin console →
              </button>
            )}
            {auth.isCurator && !auth.isAdmin && (
              <button
                onClick={() => navigate("/curator")}
                className="chip ghost"
                style={{ cursor: "pointer", border: 0, background: "transparent" }}
              >
                Curator queue →
              </button>
            )}
          </div>

          <h1
            style={{
              fontSize: 38,
              lineHeight: 1.08,
              margin: "0 0 10px",
              color: "var(--eb-15)",
              fontWeight: 700,
              letterSpacing: "-0.02em",
            }}
          >
            What are you building today?
          </h1>
          <p
            style={{
              fontSize: 15,
              color: "var(--ink-2)",
              margin: "0 0 28px",
              maxWidth: 620,
              lineHeight: 1.55,
            }}
          >
            Pick a starting point. Generator tailors the flow — scope, stories,
            and pipeline steps — to the job at hand.
          </p>

          {isTrainingDay && <TraineeQuickStart />}

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 12,
            }}
          >
            {primary.map((m) => (
              <PrimaryModeCard
                key={m.id}
                mode={m}
                onClick={() => handleMode(m.id)}
              />
            ))}
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                "minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)",
              gap: 10,
              marginTop: 12,
            }}
          >
            {secondary.map((m) => (
              <SecondaryModeCard
                key={m.id}
                mode={m}
                onClick={() => handleMode(m.id)}
              />
            ))}
            <button
              onClick={() => navigate("/templates")}
              style={{
                background: "white",
                border: "1px dashed var(--bd)",
                borderRadius: 10,
                padding: "12px 14px",
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                gap: 12,
                cursor: "pointer",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.borderColor = "var(--eb-50)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.borderColor = "var(--bd)")
              }
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  background: "var(--cb-95)",
                  color: "var(--eb-50)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  fontSize: 16,
                  fontWeight: 600,
                }}
              >
                ★
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: "var(--ink)",
                  }}
                >
                  Do a Quick Build
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: "var(--ink-3)",
                    marginTop: 1,
                  }}
                >
                  Skip the wizard — pick a saved preset
                </div>
              </div>
            </button>
          </div>
        </div>
      </section>

      {isTrainingDay && (
        <DashboardContestCard
          onLaunch={() => {
            pickMode("tdsx_only");
            navigate("/configure");
          }}
        />
      )}

      {/* ───────────── RECENT RUNS ───────────── */}
      <section style={{ padding: "40px 32px", background: "white" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto" }}>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              marginBottom: 16,
            }}
          >
            <div>
              <div className="label" style={{ marginBottom: 4 }}>
                Recent runs
              </div>
              <div style={{ fontSize: 13, color: "var(--ink-2)" }}>
                Pick up where you left off — add layers to a loaded org or
                refresh streams.
              </div>
            </div>
            <button
              className="btn ghost sm"
              onClick={() => navigate("/runs")}
            >
              View all runs →
            </button>
          </div>

          <div style={{ display: "grid", gap: 10 }}>
            {recent.length === 0 && (
              <div
                className="card"
                style={{
                  padding: 24,
                  textAlign: "center",
                  color: "var(--ink-3)",
                  fontSize: 13,
                }}
              >
                No runs yet — kick one off above.
              </div>
            )}
            {recent.map((r) => (
              <RecentRunCard
                key={r.id}
                run={r}
                onAdd={() => handleMode("continue", r.id)}
                onRefresh={() => handleMode("refresh", r.id)}
                onOpen={() => navigate(`/runs/${r.id}`)}
              />
            ))}
          </div>

          <div
            style={{
              marginTop: 20,
              display: "flex",
              gap: 22,
              fontSize: 11,
              color: "var(--ink-3)",
            }}
          >
            <div>
              <strong style={{ color: "var(--ink)" }}>
                {monthRuns.toLocaleString()}
              </strong>{" "}
              runs this month
            </div>
            <div>
              <strong style={{ color: "var(--ink)" }}>11m</strong> median run
              time
            </div>
            <div>
              <strong style={{ color: "var(--ink)" }}>{profilesCount}</strong>{" "}
              company profiles available
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// ───────────────── Auth gate + loading ─────────────────

function LandingLoading(): JSX.Element {
  return (
    <div
      style={{
        minHeight: "calc(100vh - 52px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background:
          "radial-gradient(circle at 30% 0%, #eaf3fc 0%, #f6fafd 60%, white 100%)",
      }}
    >
      <div style={{ textAlign: "center", color: "var(--ink-3)", fontSize: 12 }}>
        <div
          aria-hidden
          style={{
            width: 22,
            height: 22,
            borderRadius: 999,
            border: "2px solid var(--bd)",
            borderTopColor: "var(--eb-50)",
            margin: "0 auto 10px",
            animation: "spin 0.8s linear infinite",
          }}
        />
        Checking your session…
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
}

function ConnectGate({
  connect,
  curatorOnly,
}: {
  connect: (loginUrl?: string) => void;
  curatorOnly: boolean;
}): JSX.Element {
  const navigate = useNavigate();
  return (
    <section
      className="hero-gradient"
      style={{
        minHeight: "calc(100vh - 52px)",
        padding: "64px 32px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          maxWidth: 540,
          width: "100%",
          background: "white",
          borderRadius: 16,
          border: "1px solid var(--bd)",
          boxShadow: "0 24px 48px -16px rgba(3,45,96,.18)",
          padding: "36px 32px 32px",
          textAlign: "center",
          position: "relative",
        }}
      >
        <div
          aria-hidden
          style={{
            position: "absolute",
            top: -56,
            left: "50%",
            transform: "translateX(-50%)",
            width: 120,
            height: 120,
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: "10% 10%",
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(29,112,201,.18) 0%, rgba(29,112,201,.04) 50%, transparent 75%)",
              filter: "blur(6px)",
            }}
          />
          <img
            src="/assets/astro-waving.png"
            alt=""
            className="astro-bob"
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              filter: "drop-shadow(0 12px 18px rgba(3,45,96,.18))",
            }}
          />
        </div>

        <div style={{ height: 60 }} />

        <div
          className="chip"
          style={{
            background: "var(--cb-95)",
            borderColor: "transparent",
            color: "var(--eb-50)",
            margin: "0 auto 14px",
            display: "inline-flex",
          }}
        >
          Q Branch · Generator
        </div>
        <h1
          style={{
            fontSize: 26,
            fontWeight: 700,
            color: "var(--eb-15)",
            letterSpacing: "-0.01em",
            margin: "0 0 10px",
          }}
        >
          Connect your Salesforce org to begin
        </h1>
        <p
          style={{
            fontSize: 14,
            color: "var(--ink-2)",
            lineHeight: 1.55,
            margin: "0 auto 24px",
            maxWidth: 420,
          }}
        >
          Generator uses your Salesforce identity to load packs, queue runs,
          and write demo data into the right org. We never see your password —
          authentication runs through Salesforce OAuth.
        </p>

        <button
          onClick={() => connect()}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
            background: "var(--eb-50)",
            color: "white",
            border: 0,
            borderRadius: 10,
            padding: "12px 22px",
            fontSize: 14,
            fontWeight: 600,
            cursor: "pointer",
            boxShadow: "0 8px 22px -8px rgba(3,45,96,.4)",
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41C17.93 5.43 20 8.47 20 12c0 2.19-.79 4.19-2.1 5.39z" />
          </svg>
          Connect with Salesforce
        </button>

        {curatorOnly && (
          <div style={{ marginTop: 20, fontSize: 12, color: "var(--ink-3)" }}>
            You're signed in as a Q Branch curator without an org connection.{" "}
            <button
              onClick={() => navigate("/curator")}
              style={{
                background: "transparent",
                border: 0,
                color: "var(--eb-50)",
                cursor: "pointer",
                fontSize: 12,
                textDecoration: "underline",
              }}
            >
              Open the curator console
            </button>
          </div>
        )}

        <div
          style={{
            marginTop: 28,
            paddingTop: 18,
            borderTop: "1px solid var(--bd)",
            fontSize: 11,
            color: "var(--ink-3)",
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <span>Need a Q Branch login?</span>
          <button
            onClick={() => navigate("/team-login")}
            style={{
              background: "transparent",
              border: 0,
              color: "var(--eb-50)",
              cursor: "pointer",
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            Team sign-in →
          </button>
        </div>
      </div>
    </section>
  );
}

// ───────────────── Sub-components ─────────────────

function PrimaryModeCard({
  mode,
  onClick,
}: {
  mode: ModeOption;
  onClick: () => void;
}): JSX.Element {
  const acc = MODE_ACCENTS[mode.id];
  return (
    <button
      onClick={onClick}
      className="card"
      style={{
        padding: 20,
        textAlign: "left",
        background: "white",
        transition: "all .15s",
        cursor: "pointer",
        position: "relative",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = acc.fg;
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow =
          "0 12px 32px -10px rgba(3,45,96,.18)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "var(--bd)";
        e.currentTarget.style.transform = "none";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 14,
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            background: acc.bg,
            color: acc.fg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="currentColor"
            stroke="currentColor"
            strokeWidth={mode.id === "refresh" ? 2 : 0}
          >
            {MODE_ICONS[mode.id]}
          </svg>
        </div>
        <span
          className="chip"
          style={{
            background: acc.bg,
            color: acc.fg,
            borderColor: "transparent",
            fontSize: 10,
          }}
        >
          {mode.tag}
        </span>
      </div>
      <div
        style={{
          fontSize: 16,
          fontWeight: 600,
          color: "var(--ink)",
          marginBottom: 6,
        }}
      >
        {mode.label}
      </div>
      <p
        style={{
          fontSize: 12,
          color: "var(--ink-2)",
          lineHeight: 1.5,
          margin: "0 0 14px",
        }}
      >
        {mode.blurb}
      </p>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontSize: 11,
          color: "var(--ink-3)",
        }}
      >
        <span>{mode.time}</span>
        <span style={{ color: acc.fg, fontWeight: 600 }}>Start →</span>
      </div>
    </button>
  );
}

function SecondaryModeCard({
  mode,
  onClick,
}: {
  mode: ModeOption;
  onClick: () => void;
}): JSX.Element {
  const acc = MODE_ACCENTS[mode.id];
  return (
    <button
      onClick={onClick}
      style={{
        background: "white",
        border: "1px solid var(--bd)",
        borderRadius: 10,
        padding: "12px 14px",
        textAlign: "left",
        display: "flex",
        alignItems: "center",
        gap: 12,
        cursor: "pointer",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = acc.fg)}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--bd)")}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: acc.bg,
          color: acc.fg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="currentColor"
          stroke="currentColor"
          strokeWidth={mode.id === "refresh" ? 2 : 0}
        >
          {MODE_ICONS[mode.id]}
        </svg>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>
          {mode.label}
        </div>
        <div
          style={{
            fontSize: 11,
            color: "var(--ink-3)",
            marginTop: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {mode.blurb}
        </div>
      </div>
    </button>
  );
}

function RecentRunCard({
  run,
  onAdd,
  onRefresh,
  onOpen,
}: {
  run: Run;
  onAdd: () => void;
  onRefresh: () => void;
  onOpen: () => void;
}): JSX.Element {
  const running = run.status === "running";
  const failed = run.status === "failed";
  const swatch = failed
    ? { bg: "#f8e0e2", fg: "#ba0517", dot: "#ba0517" }
    : running
      ? { bg: "#fde8c2", fg: "#8a5200", dot: "#d97706" }
      : { bg: "#d9efe1", fg: "var(--teal)", dot: "var(--teal)" };
  const hasDC = run.layers.includes("dc");
  const hasTabnext = run.layers.includes("tabnext");

  return (
    <div
      className="card"
      style={{
        padding: "14px 18px",
        display: "grid",
        gridTemplateColumns: "minmax(0,1fr) auto",
        gap: 16,
        alignItems: "center",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          minWidth: 0,
        }}
      >
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 8,
            background: swatch.bg,
            color: swatch.fg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <span
            className={running ? "pulse-dot" : ""}
            style={{
              width: 8,
              height: 8,
              borderRadius: 999,
              background: swatch.dot,
            }}
          />
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
              rowGap: 4,
            }}
          >
            <div
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: "var(--ink)",
                whiteSpace: "nowrap",
              }}
            >
              {run.profile}
            </div>
            <span
              className="chip ghost"
              style={{ fontSize: 10, padding: "1px 7px" }}
            >
              {run.volume}
            </span>
            <span
              className="chip ghost"
              style={{ fontSize: 10, padding: "1px 7px" }}
            >
              {run.cloud}
            </span>
            {hasDC && (
              <span
                className="chip soft-violet"
                style={{ fontSize: 10, padding: "1px 7px" }}
              >
                Data 360
              </span>
            )}
            {hasTabnext && (
              <span
                className="chip soft-violet"
                style={{ fontSize: 10, padding: "1px 7px" }}
              >
                Tableau Next
              </span>
            )}
          </div>
          <div
            style={{
              fontSize: 11,
              color: "var(--ink-3)",
              marginTop: 4,
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <span>{run.org}</span>
            <span>·</span>
            <span>
              {running
                ? `Finishes ${run.completed ?? ""}`
                : failed
                  ? `Failed ${run.completed ?? ""}`
                  : `Completed ${run.completed ?? ""}`}
            </span>
          </div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 6 }}>
        {running ? (
          <button className="btn ghost sm" onClick={onOpen}>
            View progress →
          </button>
        ) : (
          <>
            <button
              className="btn ghost sm"
              onClick={onAdd}
              title="Add Data 360, SDM, apps, or Tableau Next to this existing org"
            >
              + Add to this
            </button>
            <button
              className="btn ghost sm"
              onClick={onRefresh}
              title="Refresh Data 360 streams or add another cloud"
            >
              ↻ Refresh
            </button>
          </>
        )}
        <button className="btn ghost sm" onClick={onOpen}>
          Open ↗
        </button>
      </div>
    </div>
  );
}

// ─────────────────── Trainee Quick-Start ───────────────────
//
// One-paragraph "what you'll do today" callout that sits between the
// hero copy and the mode picker on training days. Designed so a
// brand-new trainee who lands on this page at 09:01 knows exactly
// which mode card to click for Lab 1 vs Lab 2 without waiting for
// John to read out the agenda. Pure-display, zero state.

function TraineeQuickStart(): JSX.Element {
  return (
    <div
      style={{
        background: "linear-gradient(180deg, #fffbeb 0%, #fef3c7 100%)",
        border: "1px solid #fde68a",
        borderRadius: 12,
        padding: "16px 20px",
        margin: "0 0 20px",
        display: "grid",
        gridTemplateColumns: "auto 1fr",
        gap: 16,
        alignItems: "center",
      }}
    >
      <div
        aria-hidden
        style={{
          width: 44,
          height: 44,
          borderRadius: 12,
          background: "#fbbf24",
          display: "grid",
          placeItems: "center",
          color: "#78350f",
          fontWeight: 800,
          fontSize: 22,
          flexShrink: 0,
        }}
      >
        ★
      </div>
      <div>
        <div style={{ fontWeight: 700, color: "#78350f", marginBottom: 4 }}>
          Welcome, trainee — here's the plan for today
        </div>
        <div style={{ fontSize: 13, color: "#78350f", lineHeight: 1.55 }}>
          <strong>Lab 1 (~5 min):</strong> click <em>Build TDSX</em> below,
          pick the <strong>Cirrus Renewable Energy</strong> pack, and submit
          your dashboard for the prize contest. &nbsp;
          <strong>Lab 2 (~15 min):</strong> click <em>Build CRM + TDSX</em>,
          run all 6 phases, manually refresh your Data Streams, then deploy
          the <strong>TC26 Sales Cloud Dashboard</strong> from the apps tile.
        </div>
      </div>
    </div>
  );
}

// ─────────────────── Dashboard Contest Card ───────────────────
//
// Only renders when /api/runtime returns a non-empty training_mode
// (gated by `isTrainingDay` in LandingAuthed). Pitches the May-4
// dashboard contest right under the mode picker so trainees see the
// rules + prize structure before they Launch a TDSX build. Clicking
// "Start a TDSX build" pre-picks the tdsx_only mode and jumps to
// Configure — the same path Build → tdsx mode card uses, so the
// downstream Configure flow is unchanged.

function DashboardContestCard({
  onLaunch,
}: {
  onLaunch: () => void;
}): JSX.Element {
  return (
    <section
      aria-labelledby="contest-heading"
      style={{
        padding: "32px",
        background: "linear-gradient(120deg, #032d60 0%, #1a4f8b 100%)",
        color: "white",
      }}
    >
      <div
        style={{
          maxWidth: 1180,
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: "1.4fr 1fr",
          gap: 32,
          alignItems: "center",
        }}
      >
        <div>
          <div
            className="label"
            style={{
              color: "#FFD580",
              letterSpacing: 1.2,
              fontWeight: 700,
              marginBottom: 6,
            }}
          >
            Lab 1 · Dashboard Contest
          </div>
          <h2
            id="contest-heading"
            style={{
              fontSize: 30,
              fontWeight: 700,
              margin: "0 0 10px",
              letterSpacing: -0.5,
            }}
          >
            Build the best Tableau dashboard from your TDSX
          </h2>
          <p
            style={{
              fontSize: 14,
              lineHeight: 1.6,
              opacity: 0.9,
              maxWidth: 560,
              margin: "0 0 18px",
            }}
          >
            Generate a TDSX with the Cirrus Renewable Energy profile, open it
            in Tableau Desktop, and craft a dashboard that tells the strongest
            story. Three winners take home <strong>$200 gift cards</strong>.
            Submissions close at the end of Lab 1.
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 14,
              marginBottom: 22,
              fontSize: 12,
            }}
          >
            <ContestRule
              num="1"
              title="Use Cirrus pack"
              body="Profile: Cirrus Renewable Energy. Volume: Medium. Output mode: TDSX (Hyper)."
            />
            <ContestRule
              num="2"
              title="One workbook"
              body="Single .twbx export. No external joins. Pack assets only."
            />
            <ContestRule
              num="3"
              title="Story matters"
              body="Judged on insight clarity (50%), visual craft (30%), data fidelity (20%)."
            />
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              className="btn primary"
              onClick={onLaunch}
              style={{
                background: "#FFD580",
                color: "#032d60",
                fontWeight: 700,
              }}
            >
              Start a TDSX build →
            </button>
            <a
              href="https://help.tableau.com/current/pro/desktop/en-us/dashboards_create.htm"
              target="_blank"
              rel="noopener noreferrer"
              className="btn ghost"
              style={{
                color: "white",
                borderColor: "rgba(255,255,255,.4)",
              }}
            >
              Tableau dashboard guide ↗
            </a>
          </div>
        </div>
        <div
          aria-hidden="true"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            style={{
              padding: 24,
              background: "rgba(255,255,255,.08)",
              borderRadius: 18,
              border: "1px solid rgba(255,255,255,.18)",
              minWidth: 240,
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontSize: 12,
                letterSpacing: 1.4,
                fontWeight: 700,
                opacity: 0.8,
              }}
            >
              PRIZE POOL
            </div>
            <div
              style={{
                fontSize: 48,
                fontWeight: 800,
                margin: "8px 0 0",
                letterSpacing: -1.5,
                color: "#FFD580",
              }}
            >
              $600
            </div>
            <div style={{ fontSize: 13, opacity: 0.85 }}>
              3 × $200 gift cards
            </div>
            <div
              style={{
                marginTop: 16,
                paddingTop: 14,
                borderTop: "1px solid rgba(255,255,255,.18)",
                fontSize: 11,
                opacity: 0.8,
                lineHeight: 1.6,
              }}
            >
              Submit your .twbx in the<br />
              <strong>#tab-dashboard-contest</strong> Slack channel
              by lunch.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContestRule({
  num,
  title,
  body,
}: {
  num: string;
  title: string;
  body: string;
}): JSX.Element {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: "#FFD580",
          letterSpacing: 1,
          marginBottom: 4,
        }}
      >
        {num}
      </div>
      <div style={{ fontWeight: 600, marginBottom: 2 }}>{title}</div>
      <div style={{ opacity: 0.8, fontSize: 11, lineHeight: 1.5 }}>{body}</div>
    </div>
  );
}
